import Head from 'next/head'
import axios from 'axios'
import { useViewport } from 'contexts/viewport'
import { startCase } from 'lodash'

import AppHeader from 'components/app_header'
import AppFooter from 'components/app_footer'

import { TeamGamesMetaDescription, TeamGamesOGImagePath, wpTagIdBySlug, wpRoutePrefix } from 'utils/constants'
import dayjs from 'utils/dayjs'
import Link from 'next/link'

import styles from 'styles/blogListing.module.scss'
import Image from 'next/image'
import IconArrowNext from '../../../components/icon/arrow_next'
import { useState } from 'react'

interface Tag {
  description: String
  id: Number
  link: String
  slug: String
}
interface ICompProps {
  posts: Array<any>
  tags: Array<Tag>
  authors: Array<any>
  wpTag: String
}

const Tag = ({ tag }) => {
  if (!tag?.slug) {
    return null
  }

  return (
    <Link href={`${wpRoutePrefix}/${tag.slug}`}>
      <a className={`description_medium ${styles.tags}`}>{tag.slug}</a>
    </Link>
  )
}

export const Listing = ({ posts, tags, authors, wpTag }: ICompProps) => {
  const mainTag = Object.values(tags).find(({ slug }) => slug === wpTag)
  const { isMobile } = useViewport()
  const [keySearch, setKeySearch] = useState('')

  const handleKeyDown = (event: { key: string }) => {
    if (event.key === 'Enter') {
      handleSearch()
    }
  }

  const handleChangeKeySearch = (event: { target: { value: any } }) => {
    const { value } = event.target
    setKeySearch(value.trim())
  }

  const handleSearch = () => {
    if (!keySearch) return
    location.href = '/help-details/search?q=' + keySearch
  }

  return (
    <div id="news_page" className={styles.news_page}>
      <Head>
        <title>{`${startCase(wpTag)} | Team Games Blogs`}</title>
        <link rel="icon" href="/images/modal_logo.png" />
        <meta name="description" content={TeamGamesMetaDescription} />
        <meta name="og:image" content={TeamGamesOGImagePath} />
      </Head>
      <div className={styles.header_section}>
        <AppHeader transparent />
        <div className={styles.header_bg}>
          {isMobile ? (
            <div className={styles.header_bg_img}>
              <Image src="/images/wpTag/background_mobile.png" alt="Game for Every Team" layout="fill" />
            </div>
          ) : (
            <div className={styles.header_bg_img}>
              <Image src="/images/wpTag/background.png" alt="Game for Every Team" layout="fill" />
            </div>
          )}
        </div>
        <div className={styles.header_container}>
          <div className={styles.header_left}>
            <div className={styles.header_breadcrumb}>
              <div className={styles.header_help}>
                <Link href="/help-details">
                  <a>Help & Details</a>
                </Link>
              </div>
              <IconArrowNext />
              <div className={styles.header_wp_tag}>{wpTag.replace('-', ' ')}</div>
            </div>
          </div>
          <div className={styles.header_right}>
            <div className={styles.header_search_container}>
              {!isMobile && (
                <div className={styles.header_search_icon}>
                  <Image
                    src="/images/help_details/search_icon.png"
                    alt="Search Icon"
                    width={17}
                    height={17}
                    onClick={handleSearch}
                  />
                </div>
              )}
              <div className={styles.header_search_border} />
              <input
                className={styles.header_search_input}
                placeholder="Search FAQ, Announcements, etc..."
                onKeyDown={handleKeyDown}
                onChange={handleChangeKeySearch}
              />
              {isMobile && (
                <div className={styles.header_search_icon_mobile}>
                  <Image
                    src="/images/help_details/search_icon_mobile.png"
                    alt="Search Icon"
                    layout="fill"
                    onClick={handleSearch}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className={styles.container}>
        <div className={`${styles.main_content} col-md-8`}>
          {posts.map((post, postIndex) => (
            <div key={postIndex} className={styles.post}>
              <div>
                <Tag tag={mainTag} />
                {post.tags
                  .filter(id => id !== mainTag?.id)
                  .sort((a, b) => {
                    if (tags[a].slug > tags[b].slug) return 1
                    if (tags[a].slug < tags[b].slug) return -1
                    return 0
                  })
                  .map(id => (
                    <Tag key={id} tag={tags[id]} />
                  ))}
              </div>
              {/* The way dynamic routes are handled, we need to pass two props to Link:
                    - href: Which file is this in the pages folder
                    - as: How this page is shown in address bar */}
              <Link href={`${wpRoutePrefix}/${wpTag}/[slug]`} as={`${wpRoutePrefix}/${wpTag}/${post.slug}`}>
                <a>
                  <h2 className="hero" dangerouslySetInnerHTML={{ __html: post.title.rendered }} />
                </a>
              </Link>
              <p className="description_medium">
                <strong>{`By ${authors[post.author].name} | ${dayjs(post.date).format('MM/DD/YYYY')}`}</strong>
              </p>
              <div
                className={`description_medium ${styles.description}`}
                dangerouslySetInnerHTML={{ __html: post.excerpt.rendered.replace('[&hellip;]', '&hellip;') }}
              />
            </div>
          ))}
        </div>
      </div>
      <AppFooter />
    </div>
  )
}

export async function getStaticPaths() {
  const knownTags = ['news', 'announcement', 'release-notes', 'all-games', 'faq']

  // Get the paths we want to pre-render based on tags
  const paths = knownTags.map(wpTag => ({
    params: { wpTag },
  }))

  // We'll pre-render only these paths at build time.
  // { fallback: false } means other routes should 404.
  return {
    paths,
    fallback: 'blocking',
  }
}

export async function getStaticProps({ params }) {
  const { wpTag } = params
  let wpTagIds: string = wpTagIdBySlug[wpTag]
  if (!wpTagIds) {
    const tagResp = await axios.get(`https://wp.boom.tv/index.php/wp-json/wp/v2/tags?slug=${wpTag}`)
    const tagData = tagResp.data?.[0]
    if (tagData) wpTagIds = tagData.id.toString()
    else
      return {
        redirect: {
          destination: '/404', // non-existent tag https://nextjs.org/docs/basic-features/data-fetching#getstaticprops-static-generation
          permanent: false,
          // statusCode: 404, // `statusCode` must undefined or one of 301, 302, 303, 307, 308
        },
      }
  }

  // NOTE: Wordpress APIs have a max "per_page" of 100. This will need to be updated if we want more tags/authors!
  // categories=14 is the "Team Games" category
  // TODO: Figure out pagination or desired design (show only first 10 currently)
  const postsResp = await axios.get(`https://wp.boom.tv/index.php/wp-json/wp/v2/posts?categories=14&tags=${wpTagIds}`)
  const posts = postsResp.data
  // Retrieve tags and build a map for easy tag lookup
  const uniqueTagIds = {}
  for (const post of posts) {
    for (const tagId of post.tags) {
      uniqueTagIds[tagId] = true
    }
  }
  const postTagIds = Object.keys(uniqueTagIds)
  const tagsResp = await axios.get(
    `https://wp.boom.tv/index.php/wp-json/wp/v2/tags?include=${postTagIds.join(',')}&per_page=${
      postTagIds.length || 1 // 400 api error if per_page is 0 (not between 1-100)
    }`,
  )
  const tags = {}
  for (const tag of tagsResp.data) {
    tags[tag.id] = tag
  }
  // Retrieve authors and build a map for easy author lookup
  const uniqueAuthorIds = {}
  for (const post of posts) {
    uniqueAuthorIds[post.author] = true
  }
  const postAuthorIds = Object.keys(uniqueAuthorIds)
  const authorsResp = await axios.get(
    `https://wp.boom.tv/index.php/wp-json/wp/v2/users?include=${postAuthorIds.join(',')}&per_page=${
      postAuthorIds.length || 1 // 400 api error if per_page is 0 (not between 1-100)
    }`,
  )
  const authors = {}
  for (const author of authorsResp.data) {
    authors[author.id] = author
  }
  // Return props for static page generation with Next
  return {
    props: {
      posts,
      tags,
      authors,
      wpTag,
    },
    revalidate: 5,
  }
}

export default Listing
